/**
 * `HealthReportView` — print-friendly full-screen tank health report.
 *
 * One row per logged parameter: latest value, ideal range, status,
 * trend direction, and 30-day delta. "Print" calls `window.print()`;
 * the overlay itself is the only thing printed (`print:` utilities).
 */
import { useMemo } from 'react';
import type { ManualReading } from '@reefapp/shared';
import { ALL_TEST_KITS, detectTrend, summarizePeriods } from '@reefapp/shared';

interface ReportRow {
  parameter: string;
  latest: ManualReading;
  ideal: { min: number; max: number } | null;
  status: 'good' | 'low' | 'high' | 'unknown';
  trend: 'rising' | 'falling' | 'stable' | null;
  delta30d: number | null;
}

function buildRows(readings: readonly ManualReading[], nowMs: number): ReportRow[] {
  const byParam = new Map<string, ManualReading[]>();
  for (const r of readings) {
    const list = byParam.get(r.parameter);
    if (list) list.push(r);
    else byParam.set(r.parameter, [r]);
  }
  const rows: ReportRow[] = [];
  for (const [parameter, list] of byParam) {
    const sorted = [...list].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    const latest = sorted[0]!;
    const kit = ALL_TEST_KITS.find(
      (k) => k.brand === latest.brand && k.parameter === parameter,
    );
    const ideal = kit?.idealRange ?? null;
    let status: ReportRow['status'] = 'unknown';
    if (ideal) {
      status =
        latest.value < ideal.min ? 'low' : latest.value > ideal.max ? 'high' : 'good';
    }
    const trend = detectTrend(sorted, parameter);
    const periods = summarizePeriods(sorted, parameter, nowMs);
    rows.push({
      parameter,
      latest,
      ideal,
      status,
      trend: trend?.direction ?? null,
      delta30d: periods.delta30d,
    });
  }
  return rows.sort((a, b) => a.parameter.localeCompare(b.parameter));
}

const STATUS_BADGE: Record<ReportRow['status'], string> = {
  good: 'bg-green-900/30 text-green-300 border-green-700/50',
  low: 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50',
  high: 'bg-red-900/30 text-red-300 border-red-700/50',
  unknown: 'bg-reef-800 text-reef-400 border-reef-700',
};

export function HealthReportView({
  readings,
  onClose,
}: {
  readings: readonly ManualReading[];
  onClose: () => void;
}) {
  const rows = useMemo(() => buildRows(readings, Date.now()), [readings]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-reef-950 print:static print:bg-white print:text-black"
      data-testid="health-report"
    >
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-between gap-3 mb-6 print:hidden">
          <h2 className="text-xl font-bold text-reef-100">Tank Health Report</h2>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-4 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
            >
              Print
            </button>
            <button
              onClick={onClose}
              data-testid="health-report-close"
              className="px-4 py-1.5 text-xs font-semibold bg-reef-800 text-reef-200 border border-reef-700 rounded-md hover:bg-reef-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        <div className="hidden print:block mb-4">
          <h2 className="text-xl font-bold">Tank Health Report</h2>
        </div>
        <p className="text-xs text-reef-500 print:text-gray-600 mb-4">
          Generated {new Date().toLocaleString()} · {rows.length} parameter
          {rows.length !== 1 ? 's' : ''} tracked
        </p>

        {rows.length === 0 ? (
          <p className="text-sm text-reef-400">No readings logged yet.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.parameter}
                data-testid={`health-row-${row.parameter}`}
                className="rounded-xl border border-reef-800 bg-reef-900 p-4 print:border-gray-300 print:bg-white"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-reef-100 print:text-black">
                    {row.parameter}
                  </h3>
                  <span
                    className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border ${STATUS_BADGE[row.status]}`}
                  >
                    {row.status === 'good'
                      ? 'In range'
                      : row.status === 'low'
                        ? 'Below range'
                        : row.status === 'high'
                          ? 'Above range'
                          : 'No range set'}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-reef-500 print:text-gray-500">Latest</div>
                    <div className="font-semibold text-reef-200 print:text-black">
                      {row.latest.value} {row.latest.unit}
                    </div>
                    <div className="text-[10px] text-reef-500 print:text-gray-500">
                      {new Date(row.latest.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-reef-500 print:text-gray-500">Ideal</div>
                    <div className="font-semibold text-reef-200 print:text-black">
                      {row.ideal ? `${row.ideal.min}–${row.ideal.max}` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-reef-500 print:text-gray-500">Trend</div>
                    <div className="font-semibold text-reef-200 print:text-black">
                      {row.trend === 'rising'
                        ? '▲ Rising'
                        : row.trend === 'falling'
                          ? '▼ Falling'
                          : row.trend === 'stable'
                            ? '— Stable'
                            : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-reef-500 print:text-gray-500">30d change</div>
                    <div className="font-semibold text-reef-200 print:text-black">
                      {row.delta30d !== null
                        ? `${row.delta30d >= 0 ? '+' : ''}${row.delta30d.toFixed(2)}`
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
